import Cookies from "js-cookie";

// The Auth Portal environments this one website accepts (SID-2662). Staging and
// sandbox users sign in at different portals, which set differently named
// cookies on the shared domain, so the user's environment is decided at runtime
// from their cookies instead of being fixed at build time. URLs only ever come
// from this list, never from the address bar, so a link can't send tokens to
// another server.
//
// The build's own REACT_APP_AUTH_URL / SSO_API_BASE_URL / TOKEN_KEY /
// REF_TOKEN_KEY are the first environment; REACT_APP_SANDBOX_* add sandbox to a
// build that serves both (the staging build). A build with one environment
// behaves exactly as before.
export const buildAuthEnvs = (env = process.env) => {
  const envs = [];
  const add = (id, authUrl, ssoApi, jwtKey, refreshKey) => {
    if (!authUrl || !ssoApi || !jwtKey || !refreshKey) return;
    if (envs.some((e) => e.id === id || e.jwtKey === jwtKey)) return;
    envs.push({ id, authUrl: authUrl.replace(/\/+$/, ""), ssoApi: ssoApi.replace(/\/+$/, ""), jwtKey, refreshKey });
  };
  add(
    env.REACT_APP_AUTH_ENV_ID || "main",
    env.REACT_APP_AUTH_URL,
    env.REACT_APP_SSO_API_BASE_URL,
    env.REACT_APP_TOKEN_KEY,
    env.REACT_APP_REF_TOKEN_KEY,
  );
  add(
    "sbx",
    env.REACT_APP_SANDBOX_AUTH_URL,
    env.REACT_APP_SANDBOX_SSO_API_BASE_URL,
    env.REACT_APP_SANDBOX_TOKEN_KEY,
    env.REACT_APP_SANDBOX_REF_TOKEN_KEY,
  );
  return envs;
};

export const AUTH_ENVS = buildAuthEnvs();

// The environment the user belongs to. An environment with a JWT wins over one
// with only a refresh token, since a portal sign-in always sets the JWT; among
// equals, the one the user last used. With no cookies at all, the one they last
// signed in with, else the build's first (staging), as before SID-2662.
export const resolveAuthEnv = (envs, getCookie, rememberedId) => {
  if (!envs.length) return null;
  const pick = (candidates) =>
    candidates.find((e) => e.id === rememberedId) ?? candidates[0] ?? null;
  const withJwt = envs.filter((e) => getCookie(e.jwtKey));
  if (withJwt.length) return pick(withJwt);
  const withRefresh = envs.filter((e) => getCookie(e.refreshKey));
  if (withRefresh.length) return pick(withRefresh);
  return envs.find((e) => e.id === rememberedId) ?? envs[0];
};

// The environment the user last signed in with, so a return visit after both
// tokens have expired, or after logging out, goes straight back to the right
// portal. Only an id from AUTH_ENVS is ever stored or used.
const LAST_AUTH_ENV_KEY = "authEnv";

export const getRememberedAuthEnvId = () => {
  try {
    return localStorage.getItem(LAST_AUTH_ENV_KEY);
  } catch {
    return null;
  }
};

export const rememberAuthEnv = (id) => {
  try {
    localStorage.setItem(LAST_AUTH_ENV_KEY, id);
  } catch {
    // Storage unavailable: with no cookies left, the user goes to the first
    // environment's portal.
  }
};

export const getAuthEnv = () =>
  resolveAuthEnv(AUTH_ENVS, (name) => Cookies.get(name), getRememberedAuthEnvId());

// The top bar's "Demo" button and "ACT Verify QR Generator" link point at the
// staging demo sites, so sandbox and production users don't get them
// (SID-2663).
const NO_DEMO_LINK_ENV_IDS = new Set(["sbx", "prd"]);

export const showsDemoLinks = (env) => env != null && !NO_DEMO_LINK_ENV_IDS.has(env.id);
